import { useRef, useState, useEffect, useCallback } from "react";
import {
  calcularFechasCitaDesdeDrop,
  calcularFechaFinDesdeResize,
  duracionCitaMs,
} from "../utils/agendaCalendario";

const UMBRAL_MOVIMIENTO_PX = 4;

/**
 * Arrastre con pointer: zona superior = mover; inferior = redimensionar duración.
 */
export function useAgendaCitaPointer({ onGuardar, guardandoRef, interactuandoRef: interactuandoExterno }) {
  const activeRef = useRef(null);
  const huboMovimientoRef = useRef(false);
  const columnasRef = useRef({});
  const interactuandoInterno = useRef(false);
  const interactuandoRef = interactuandoExterno || interactuandoInterno;
  const [preview, setPreview] = useState(null);
  const [activaId, setActivaId] = useState(null);

  const registrarColumna = useCallback((key, el, diaDate) => {
    if (el) columnasRef.current[key] = { el, key, diaDate };
    else delete columnasRef.current[key];
  }, []);

  const findColumna = useCallback((clientX, clientY) => {
    let found = null;
    for (const col of Object.values(columnasRef.current)) {
      const r = col.el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
        found = col;
      }
    }
    return found;
  }, []);

  const cancelar = useCallback(() => {
    activeRef.current = null;
    interactuandoRef.current = false;
    setActivaId(null);
    setPreview(null);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const finalizar = useCallback(async () => {
    const st = activeRef.current;
    const prev = preview;
    const hubo = huboMovimientoRef.current;
    cancelar();
    huboMovimientoRef.current = false;
    if (!st || !hubo || !prev || guardandoRef?.current) return;
    guardandoRef.current = true;
    try {
      await onGuardar(st.cita, prev.fecha_inicio, prev.fecha_fin);
    } finally {
      guardandoRef.current = false;
    }
  }, [preview, onGuardar, guardandoRef, cancelar]);

  useEffect(() => {
    const onMove = (e) => {
      const st = activeRef.current;
      if (!st) return;
      if (
        Math.abs(e.clientY - st.startY) > UMBRAL_MOVIMIENTO_PX ||
        Math.abs(e.clientX - st.startX) > UMBRAL_MOVIMIENTO_PX
      ) {
        huboMovimientoRef.current = true;
      }
      const colHit = findColumna(e.clientX, e.clientY);
      const col = colHit?.el || st.columnEl;
      const diaDate = colHit?.diaDate || st.diaDate;
      if (st.mode === "move") {
        const { fecha_inicio, fecha_fin } = calcularFechasCitaDesdeDrop(
          diaDate,
          e.clientY,
          col,
          st.duracionMs
        );
        setPreview({ id: st.cita.id, fecha_inicio, fecha_fin });
      } else {
        const iniIso = st.cita.fecha_inicio;
        const fecha_fin = calcularFechaFinDesdeResize(diaDate, e.clientY, col, iniIso);
        setPreview({ id: st.cita.id, fecha_inicio: iniIso, fecha_fin });
      }
    };

    const onUp = () => {
      if (!activeRef.current) return;
      finalizar();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [findColumna, finalizar]);

  const getColumnaEl = useCallback((diaKey) => columnasRef.current[diaKey]?.el ?? null, []);

  const iniciar = useCallback(
    (e, cita, mode, columnEl, diaKey, diaDate) => {
      if (guardandoRef?.current) return;
      const col = columnEl || columnasRef.current[diaKey]?.el;
      if (!col) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        e.currentTarget?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      huboMovimientoRef.current = false;
      interactuandoRef.current = true;
      document.body.style.userSelect = "none";
      document.body.style.cursor = mode === "move" ? "grabbing" : "ns-resize";
      activeRef.current = {
        mode,
        cita,
        columnEl: col,
        diaKey,
        diaDate,
        duracionMs: duracionCitaMs(cita),
        startY: e.clientY,
        startX: e.clientX,
      };
      setActivaId(cita.id);
      setPreview({
        id: cita.id,
        fecha_inicio: cita.fecha_inicio,
        fecha_fin: cita.fecha_fin,
      });
    },
    [guardandoRef]
  );

  const iniciarMover = useCallback(
    (e, cita, columnEl, diaKey, diaDate) => iniciar(e, cita, "move", columnEl, diaKey, diaDate),
    [iniciar]
  );

  const iniciarResize = useCallback(
    (e, cita, columnEl, diaKey, diaDate) => iniciar(e, cita, "resize", columnEl, diaKey, diaDate),
    [iniciar]
  );

  const citaConPreview = useCallback(
    (cita) => {
      if (preview?.id === cita.id) {
        return { ...cita, fecha_inicio: preview.fecha_inicio, fecha_fin: preview.fecha_fin };
      }
      return cita;
    },
    [preview]
  );

  return {
    registrarColumna,
    getColumnaEl,
    iniciarMover,
    iniciarResize,
    preview,
    activaId,
    huboMovimientoRef,
    citaConPreview,
    interactuandoRef,
    cancelar,
  };
}
